import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { ThemeEvents } from '@theme/events';
import { isClickedOutside, onAnimationEnd, throttle } from '@theme/utilities';

const AUTO_OPEN_SESSION_KEY = 'tinker_marketing_popup_auto_opened';
const CLICK_OPEN_SESSION_PREFIX = 'tinker_marketing_popup_click_opened_';

/** @param {string} value */
function safeParseJSON(value) {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

/** @param {unknown} op */
function normalizeOp(op) {
  if (typeof op !== 'string') return '==';
  const v = op.trim().toLowerCase();
  if (v === '=' || v === 'eq') return '==';
  if (v === '!=' || v === 'neq') return '!=';
  if (v === '<' || v === 'lt') return '<';
  if (v === '<=' || v === 'lte') return '<=';
  if (v === '>' || v === 'gt') return '>';
  if (v === '>=' || v === 'gte') return '>=';
  if (v === '==' || v === '===') return '==';
  return '==';
}

/**
 * @param {unknown} actual
 * @param {unknown} op
 * @param {unknown} expected
 */
function compareNumber(actual, op, expected) {
  const a = Number(actual);
  const e = Number(expected);
  if (!Number.isFinite(a) || !Number.isFinite(e)) return false;
  const o = normalizeOp(op);
  if (o === '==') return a === e;
  if (o === '!=') return a !== e;
  if (o === '<') return a < e;
  if (o === '<=') return a <= e;
  if (o === '>') return a > e;
  if (o === '>=') return a >= e;
  return false;
}

function getCurrentTemplate() {
  const main = document.querySelector('#MainContent');
  if (!(main instanceof HTMLElement)) return '';
  return (main.dataset.template || '').trim();
}

function isDesktopViewport() {
  return matchMedia('(min-width: 750px)').matches;
}

function isMobileViewport() {
  return matchMedia('(max-width: 749px)').matches;
}

/** @param {unknown} resource */
function looksLikeCart(resource) {
  if (!resource || typeof resource !== 'object') return false;
  const anyResource = /** @type {any} */ (resource);
  if (Array.isArray(anyResource.items)) return true;
  if (Number.isFinite(Number(anyResource.item_count))) return true;
  if (Number.isFinite(Number(anyResource.total_price))) return true;
  return false;
}

const cartCache = {
  cart: /** @type {any | null} */ (null),
  updatedAt: 0,
  fetchPromise: /** @type {Promise<any> | null} */ (null),
  listenerAttached: false,
};

const resumeSubmitGuards = new WeakSet();
const resumeClickGuards = new WeakSet();
const RESUME_ACTION_HASH = '#marketing-popup-resume';

/** @param {Element} target */
function isInResumeClickGuard(target) {
  let el = target;
  while (el) {
    if (resumeClickGuards.has(el)) return true;
    const parent = el.parentElement;
    if (!(parent instanceof Element)) return false;
    el = parent;
  }
  return false;
}

/** @param {HTMLAnchorElement} link */
function isResumeActionLink(link) {
  const raw = (link.getAttribute('href') || '').trim();
  if (!raw) return false;
  if (raw === RESUME_ACTION_HASH) return true;
  try {
    const url = new URL(raw, window.location.href);
    return url.hash === RESUME_ACTION_HASH;
  } catch {
    return false;
  }
}

function attachCartCacheListener() {
  if (cartCache.listenerAttached) return;
  cartCache.listenerAttached = true;

  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    const e = /** @type {any} */ (event);
    const resource = e?.detail?.resource;
    if (looksLikeCart(resource)) {
      cartCache.cart = resource;
      cartCache.updatedAt = Date.now();
      return;
    }

    if (resource && typeof resource === 'object') {
      cartCache.updatedAt = 0;
      void getCartForConditions({ forceRefresh: true });
    }
  });
}

async function getCartFromNetwork() {
  const response = await fetch('/cart.js', {
    cache: 'no-store',
    headers: {
      'cache-control': 'no-store',
    },
  });
  if (!response.ok) return null;
  return response.json();
}

/**
 * @param {{ forceRefresh?: boolean; waitForUpdateMs?: number }=} options
 */
async function getCartForConditions(options = {}) {
  const forceRefresh = options.forceRefresh === true;
  const waitForUpdateMs = Math.max(0, Number(options.waitForUpdateMs || 0) || 0);
  const maxAgeMs = 5000;
  if (!forceRefresh && looksLikeCart(cartCache.cart) && Date.now() - cartCache.updatedAt < maxAgeMs) {
    return cartCache.cart;
  }

  const startedAt = Date.now();

  const waitForCartUpdate =
    waitForUpdateMs > 0
      ? new Promise((resolve) => {
          if (looksLikeCart(cartCache.cart) && cartCache.updatedAt > startedAt) {
            resolve(cartCache.cart);
            return;
          }

          let done = false;
          const timer = window.setTimeout(() => {
            if (done) return;
            done = true;
            document.removeEventListener(ThemeEvents.cartUpdate, handler);
            resolve(null);
          }, waitForUpdateMs);

          /** @param {Event} event */
          const handler = (event) => {
            if (done) return;
            const e = /** @type {any} */ (event);
            const resource = e?.detail?.resource;
            if (!looksLikeCart(resource)) return;
            done = true;
            window.clearTimeout(timer);
            document.removeEventListener(ThemeEvents.cartUpdate, handler);
            cartCache.cart = resource;
            cartCache.updatedAt = Date.now();
            resolve(resource);
          };

          document.addEventListener(ThemeEvents.cartUpdate, handler);
        })
      : Promise.resolve(null);

  if (!cartCache.fetchPromise) {
    cartCache.fetchPromise = getCartFromNetwork()
      .then((cart) => {
        if (looksLikeCart(cart)) {
          cartCache.cart = cart;
          cartCache.updatedAt = Date.now();
        }
        return cart;
      })
      .catch(() => null)
      .finally(() => {
        cartCache.fetchPromise = null;
      });
  }

  const fetchPromise = cartCache.fetchPromise;
  if (!fetchPromise) return null;

  const first = await Promise.race([fetchPromise, waitForCartUpdate]);
  if (looksLikeCart(first)) return first;

  const cartFromFetch = await fetchPromise;
  if (!looksLikeCart(cartFromFetch)) return null;

  const cartFromEvent = await Promise.race([
    waitForCartUpdate,
    new Promise((resolve) => window.setTimeout(() => resolve(null), 300)),
  ]);

  return looksLikeCart(cartFromEvent) ? cartFromEvent : cartFromFetch;
}

/**
 * @typedef {{ delaySeconds: number; priority: number }} AutoOpenConfig
 */

const popupManager = {
  /** @type {MarketingPopup | null} */
  current: null,
  /** @type {Map<MarketingPopup, AutoOpenConfig>} */
  candidates: new Map(),
  /** @type {number | null} */
  autoOpenTimer: null,
  autoOpenScheduled: false,

  /**
   * @param {MarketingPopup} popup
   * @param {AutoOpenConfig} config
   */
  registerAutoOpenCandidate(popup, config) {
    this.candidates.set(popup, config);
    if (this.autoOpenScheduled) return;
    this.autoOpenScheduled = true;

    setTimeout(() => {
      this.autoOpenScheduled = false;
      this.scheduleAutoOpen();
    });
  },

  /** @param {MarketingPopup} popup */
  unregister(popup) {
    this.candidates.delete(popup);
    if (this.current === popup) this.current = null;
  },

  scheduleAutoOpen() {
    if (this.autoOpenTimer != null) return;
    if (this.hasAutoOpenedThisSession()) return;

    const entries = Array.from(this.candidates.entries())
      .filter(([popup]) => popup.isConnected)
      .filter(([popup]) => popup.dataset.enabled !== 'false')
      .filter(([popup]) => popup.dataset.autoOpen !== 'false')
      .filter(([popup]) => (popup.dataset.autoOpenTrigger || 'delay') === 'delay')
      .filter(([, cfg]) => cfg.delaySeconds > 0);

    if (!entries.length) return;

    entries.sort((a, b) => {
      const pa = a[1].priority;
      const pb = b[1].priority;
      if (pa !== pb) return pb - pa;
      return a[1].delaySeconds - b[1].delaySeconds;
    });

    const first = entries[0];
    if (!first) return;
    const [target, cfg] = first;
    this.autoOpenTimer = window.setTimeout(() => {
      this.autoOpenTimer = null;
      if (!target.isConnected) return;
      target.showDialog({ isAuto: true });
    }, cfg.delaySeconds * 1000);
  },

  hasAutoOpenedThisSession() {
    try {
      return sessionStorage.getItem(AUTO_OPEN_SESSION_KEY) === 'true';
    } catch {
      return false;
    }
  },

  /**
   * @param {MarketingPopup} popup
   */
  markAutoOpened(popup) {
    const once = popup.dataset.autoOpenOnce !== 'false';
    if (!once) return;
    try {
      sessionStorage.setItem(AUTO_OPEN_SESSION_KEY, 'true');
    } catch {
      return;
    }
  },

  /**
   * @param {MarketingPopup} popup
   */
  canAutoOpen(popup) {
    if (popup.dataset.autoOpen === 'false') return false;
    if (popup.dataset.autoOpenOnce !== 'false' && this.hasAutoOpenedThisSession()) return false;
    return true;
  },

  /**
   * @param {MarketingPopup} popup
   */
  notifyOpened(popup) {
    this.current = popup;
  },

  /**
   * @param {MarketingPopup} popup
   */
  notifyClosed(popup) {
    if (this.current === popup) this.current = null;
  },
};

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 */

/** @extends {Component<Refs>} */
export class MarketingPopup extends Component {
  requiredRefs = ['dialog'];

  #previousScrollY = 0;
  #locked = false;
  #abortController = new AbortController();
  #isDesignMode = false;
  #lastFocusedElement = /** @type {HTMLElement | null} */ (null);
  #scrollHandler = /** @type {(((...args: any[]) => void) & { cancel?: () => void }) | null} */ (null);
  #exitIntentHandler = /** @type {(((...args: any[]) => void)) | null} */ (null);
  #clickTriggers = /** @type {any[] | null} */ (null);
  #pendingAction = /** @type {{ element: Element } | null} */ (null);

  connectedCallback() {
    super.connectedCallback();

    this.#isDesignMode = Boolean(window.Shopify?.designMode);

    this.refs.dialog.addEventListener(
      'cancel',
      (event) => {
        event.preventDefault();
        const allowEscape = this.dataset.closeOnEscape !== 'false';
        if (!allowEscape) return;
        this.closeDialog();
      },
      { signal: this.#abortController.signal }
    );

    this.refs.dialog.addEventListener(
      'close',
      () => {
        removeTrapFocus();
        popupManager.notifyClosed(this);
        if (this.#locked) this.#unlockScroll();
        this.#restoreFocus();
        this.#pendingAction = null;
      },
      { signal: this.#abortController.signal }
    );

    this.refs.dialog.addEventListener(
      'click',
      async (event) => {
        if (!(event.target instanceof Element)) return;
        const link = event.target.closest('a[href]');
        if (!(link instanceof HTMLAnchorElement)) return;
        if (!isResumeActionLink(link)) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const pending = this.#pendingAction;
        this.#pendingAction = null;
        await this.closeDialog();
        if (pending?.element) this.#resumeDefaultAction(pending.element);
      },
      { capture: true, signal: this.#abortController.signal }
    );

    if (this.#isDesignMode) {
      document.addEventListener(
        'shopify:block:select',
        (event) => {
          if (!(event.target instanceof HTMLElement)) return;
          if (event.target.closest('marketing-popup') !== this) return;
          this.showDialog({ isAuto: false, isEditor: true });
        },
        { signal: this.#abortController.signal }
      );

      document.addEventListener(
        'shopify:block:deselect',
        (event) => {
          if (!(event.target instanceof HTMLElement)) return;
          if (event.target.closest('marketing-popup') !== this) return;
          this.closeDialog();
        },
        { signal: this.#abortController.signal }
      );

      if (this.#getEditorOpenState()) {
        setTimeout(() => {
          this.showDialog({ isAuto: false, isEditor: true });
        });
      }
    }

    const enabled = this.dataset.enabled !== 'false';
    if (!enabled) return;

    const delaySeconds = Number(this.dataset.delaySeconds || 0);
    const triggerSelector = (this.dataset.triggerSelector || '').trim();
    const openOnClick = this.dataset.openOnClick !== 'false';
    const allowAutoOpen = this.dataset.autoOpen !== 'false';
    const priority = Number(this.dataset.priority || 0);
    const autoOpenTrigger = (this.dataset.autoOpenTrigger || 'delay').trim() || 'delay';
    const clickMode = (this.dataset.clickMode || 'basic').trim() || 'basic';
    const clickTriggersJson = (this.dataset.clickTriggersJson || '').trim();

    if (openOnClick && clickMode === 'advanced' && clickTriggersJson) {
      const parsed = safeParseJSON(clickTriggersJson);
      this.#clickTriggers = Array.isArray(parsed) ? parsed : null;
    }
    const hasAdvancedClickTriggers = Boolean(this.#clickTriggers && this.#clickTriggers.length);

    if (!this.#isDesignMode) {
      if (allowAutoOpen && autoOpenTrigger === 'delay' && delaySeconds > 0) {
        popupManager.registerAutoOpenCandidate(this, { delaySeconds, priority });
      }

      if (allowAutoOpen && autoOpenTrigger === 'scroll_percent') {
        const threshold = Math.max(1, Math.min(100, Number(this.dataset.scrollPercent || 0) || 0));
        if (threshold > 0 && popupManager.canAutoOpen(this)) {
          const handler = throttle(() => {
            if (!this.isConnected) return;
            if (!popupManager.canAutoOpen(this)) return;
            const max = document.documentElement.scrollHeight - window.innerHeight;
            const pct = max > 0 ? (window.scrollY / max) * 100 : 0;
            if (pct < threshold) return;

            window.removeEventListener('scroll', handler);
            handler.cancel?.();
            this.#scrollHandler = null;
            this.showDialog({ isAuto: true });
          }, 200);

          this.#scrollHandler = handler;
          window.addEventListener('scroll', handler, { passive: true });
        }
      }

      if (allowAutoOpen && autoOpenTrigger === 'exit_intent') {
        const supportsExitIntent = matchMedia('(hover: hover) and (pointer: fine)').matches;
        const sensitivity = Math.max(0, Math.min(30, Number(this.dataset.exitIntentSensitivity || 0) || 0));
        if (supportsExitIntent && popupManager.canAutoOpen(this)) {
          const handler = (/** @type {MouseEvent} */ event) => {
            if (!(event instanceof MouseEvent)) return;
            if (!this.isConnected) return;
            if (!popupManager.canAutoOpen(this)) return;
            if (event.relatedTarget != null) return;
            if (event.clientY > sensitivity) return;

            document.removeEventListener('mouseout', handler);
            this.#exitIntentHandler = null;
            this.showDialog({ isAuto: true });
          };

          this.#exitIntentHandler = handler;
          document.addEventListener('mouseout', handler, { passive: true });
        }
      }
    }

    if (openOnClick && (hasAdvancedClickTriggers || triggerSelector)) {
      attachCartCacheListener();
      const basicConditionType = (this.dataset.clickConditionType || 'none').trim();
      const mayNeedCart =
        basicConditionType.startsWith('cart_') ||
        (hasAdvancedClickTriggers && this.#clickTriggers?.some((t) => JSON.stringify(t || {}).includes('cart_')));
      if (mayNeedCart) void getCartForConditions();

      document.addEventListener(
        'click',
        (event) => {
          const evt = /** @type {any} */ (event);
          if (evt.__tinkerMarketingPopupHandled) return;
          if (!(event.target instanceof Element)) return;
          if (isInResumeClickGuard(event.target)) return;

          if (hasAdvancedClickTriggers) {
            const matched = this.#matchAdvancedTrigger(event.target);
            if (!matched) return;
            this.#handleMatchedTriggerSync(event, evt, matched.element, matched.trigger);
            return;
          }

          if (!triggerSelector) return;
          const matched = event.target.closest(triggerSelector);
          if (!(matched instanceof Element)) return;
          this.#handleMatchedBasicTriggerSync(event, evt, matched);
        },
        { capture: true, signal: this.#abortController.signal }
      );

      document.addEventListener(
        'submit',
        (event) => {
          const evt = /** @type {any} */ (event);
          if (evt.__tinkerMarketingPopupHandled) return;
          const form = event.target;
          if (!(form instanceof HTMLFormElement)) return;
          if (resumeSubmitGuards.has(form)) return;

          const submitter = /** @type {any} */ (event).submitter;
          if (!(submitter instanceof Element)) return;
          if (isInResumeClickGuard(submitter)) return;

          if (hasAdvancedClickTriggers) {
            const matched = this.#matchAdvancedTrigger(submitter);
            if (!matched) return;
            this.#handleMatchedTriggerSync(event, evt, matched.element, matched.trigger);
            return;
          }

          if (!triggerSelector) return;
          if (!submitter.closest(triggerSelector)) return;
          this.#handleMatchedBasicTriggerSync(event, evt, submitter);
        },
        { capture: true, signal: this.#abortController.signal }
      );
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();

    if (this.#scrollHandler) {
      window.removeEventListener('scroll', this.#scrollHandler);
      this.#scrollHandler = null;
    }

    if (this.#exitIntentHandler) {
      document.removeEventListener('mouseout', this.#exitIntentHandler);
      this.#exitIntentHandler = null;
    }

    if (this.#locked) {
      this.#unlockScroll();
    }

    removeTrapFocus();
    popupManager.unregister(this);
  }

  /**
   * @param {{ isAuto?: boolean; isEditor?: boolean }=} options
   */
  async showDialog(options = {}) {
    const isAuto = options.isAuto === true;
    const isEditor = options.isEditor === true;
    const { dialog } = this.refs;
    if (dialog.open) return;

    const active = document.activeElement;
    this.#lastFocusedElement = active instanceof HTMLElement ? active : null;

    if (isAuto) {
      if (!popupManager.canAutoOpen(this)) return;
      if (this.#shouldBlockAutoOpen()) return;
    }

    if (!isEditor && popupManager.current && popupManager.current !== this) {
      await popupManager.current.closeDialog();
    }

    const shouldLockScroll = this.hasAttribute('lock-scroll');

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    requestAnimationFrame(() => {
      if (shouldLockScroll) {
        this.#lockScroll(scrollY);
      } else {
        this.#locked = false;
      }

      dialog.showModal();

      if (isAuto) {
        popupManager.markAutoOpened(this);
      }

      if (!isEditor && !this.#isDesignMode) {
        this.#setCookieIfNeeded();
      }
      if (this.#isDesignMode) {
        this.#setEditorOpenState(true);
      }

      this.dispatchEvent(new CustomEvent('dialog:open'));
      this.addEventListener('click', this.#handleClick);
      popupManager.notifyOpened(this);
      trapFocus(dialog);
      this.#focusForOpen();
    });
  }

  closeDialog = async () => {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);

    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, { subtree: false });

    if (this.#locked) {
      this.#unlockScroll();
    }

    dialog.close();
    dialog.classList.remove('dialog-closing');
    this.dispatchEvent(new CustomEvent('dialog:close'));

    if (this.#isDesignMode) {
      this.#setEditorOpenState(false);
    }

    this.#restoreFocus();
  };

  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog({ isAuto: false });
    }
  };

  #clickOpenOncePerSessionEnabled() {
    return this.dataset.clickOpenOncePerSession === 'true';
  }

  #clickOnceStorageKey() {
    const id = (this.dataset.popupId || this.getAttribute('data-popup-id') || this.id || '').replace(/[^a-zA-Z0-9_]/g, '_');
    return `${CLICK_OPEN_SESSION_PREFIX}${id}`;
  }

  #hasOpenedOnClickThisSession() {
    try {
      return sessionStorage.getItem(this.#clickOnceStorageKey()) === 'true';
    } catch {
      return false;
    }
  }

  #markOpenedOnClickThisSession() {
    try {
      sessionStorage.setItem(this.#clickOnceStorageKey(), 'true');
    } catch {
      return;
    }
  }

  /** @param {unknown} raw */
  #parseMoneyToCents(raw) {
    const v = String(raw || '').trim().replace(',', '.');
    const num = Number(v);
    if (!Number.isFinite(num)) return null;
    return Math.round(num * 100);
  }

  /** @param {Element} target */
  #matchAdvancedTrigger(target) {
    if (!this.#clickTriggers || !this.#clickTriggers.length) return null;
    for (const trigger of this.#clickTriggers) {
      const selector = typeof trigger?.selector === 'string' ? trigger.selector.trim() : '';
      if (!selector) continue;
      const matched = target.closest(selector);
      if (!(matched instanceof Element)) continue;
      return { element: matched, trigger };
    }
    return null;
  }

  /** @param {boolean} triggerOncePerSession */
  #shouldBlockClickOpenSync(triggerOncePerSession) {
    if (!this.#clickOpenOncePerSessionEnabled() && !triggerOncePerSession) return false;
    return this.#hasOpenedOnClickThisSession();
  }

  /** @returns {any | null} */
  #getCachedCartForConditions() {
    const maxAgeMs = 5000;
    if (!cartCache.cart) return null;
    if (Date.now() - cartCache.updatedAt > maxAgeMs) return null;
    if (!looksLikeCart(cartCache.cart)) return null;
    return cartCache.cart;
  }

  /** @returns {boolean | null} */
  #evaluateBasicClickConditionSync() {
    const type = (this.dataset.clickConditionType || 'none').trim() || 'none';
    if (type === 'none') return true;

    if (type === 'customer_logged_in') return this.dataset.customerLoggedIn === 'true';
    if (type === 'customer_logged_out') return this.dataset.customerLoggedIn !== 'true';
    if (type === 'template_equals') {
      const expected = String(this.dataset.clickConditionValue || '').trim();
      if (!expected) return false;
      return getCurrentTemplate() === expected;
    }
    if (type === 'url_contains') {
      const expected = String(this.dataset.clickConditionValue || '').trim();
      if (!expected) return false;
      return window.location.href.includes(expected);
    }
    if (type === 'device_desktop') return isDesktopViewport();
    if (type === 'device_mobile') return isMobileViewport();

    if (
      type === 'cart_total_under' ||
      type === 'cart_total_over' ||
      type === 'cart_item_count_under' ||
      type === 'cart_item_count_over'
    ) {
      const cart = this.#getCachedCartForConditions();
      if (!cart || typeof cart !== 'object') return null;

      if (type === 'cart_total_under' || type === 'cart_total_over') {
        const threshold = this.#parseMoneyToCents(this.dataset.clickConditionValue);
        if (threshold == null) return false;
        const total = Number(cart.total_price);
        if (!Number.isFinite(total)) return false;
        return type === 'cart_total_under' ? total < threshold : total > threshold;
      }

      const threshold = Number(String(this.dataset.clickConditionValue || '').trim());
      if (!Number.isFinite(threshold)) return false;
      const count = Number.isFinite(Number(cart.item_count))
        ? Number(cart.item_count)
        : Array.isArray(cart.items)
          ? cart.items.reduce((/** @type {number} */ sum, /** @type {any} */ item) => sum + (Number(item?.quantity) || 0), 0)
          : 0;
      return type === 'cart_item_count_under' ? count < threshold : count > threshold;
    }

    return false;
  }

  /**
   * @param {any} node
   * @returns {boolean | null}
   */
  #evaluateConditionNodeSync(node) {
    if (node == null) return true;
    const cart = this.#getCachedCartForConditions();

    if (Array.isArray(node)) {
      let sawUnknown = false;
      for (const child of node) {
        const r = this.#evaluateConditionNodeSync(child);
        if (r === false) return false;
        if (r == null) sawUnknown = true;
      }
      return sawUnknown ? null : true;
    }

    if (typeof node !== 'object') return Boolean(node);

    const anyNode = /** @type {any} */ (node);

    if (Array.isArray(anyNode.all)) {
      let sawUnknown = false;
      for (const child of anyNode.all) {
        const r = this.#evaluateConditionNodeSync(child);
        if (r === false) return false;
        if (r == null) sawUnknown = true;
      }
      return sawUnknown ? null : true;
    }

    if (Array.isArray(anyNode.any)) {
      let sawUnknown = false;
      for (const child of anyNode.any) {
        const r = this.#evaluateConditionNodeSync(child);
        if (r === true) return true;
        if (r == null) sawUnknown = true;
      }
      return sawUnknown ? null : false;
    }

    if (anyNode.not != null) {
      const r = this.#evaluateConditionNodeSync(anyNode.not);
      if (r == null) return null;
      return !r;
    }

    const type = typeof anyNode.type === 'string' ? anyNode.type.trim() : '';
    if (!type) return false;

    if (type === 'customer_logged_in') {
      const expected = anyNode.value == null ? true : Boolean(anyNode.value);
      const actual = this.dataset.customerLoggedIn === 'true';
      return actual === expected;
    }
    if (type === 'page_template') {
      const expected = String(anyNode.value || '').trim();
      if (!expected) return false;
      return getCurrentTemplate() === expected;
    }
    if (type === 'url_contains') {
      const expected = String(anyNode.value || '').trim();
      if (!expected) return false;
      return window.location.href.includes(expected);
    }
    if (type === 'path_equals') {
      const expected = String(anyNode.value || '').trim();
      if (!expected) return false;
      return window.location.pathname === expected;
    }
    if (type === 'device_desktop') return isDesktopViewport();
    if (type === 'device_mobile') return isMobileViewport();

    if (type === 'cart_total_price') {
      if (!cart) return null;
      return compareNumber(cart.total_price, anyNode.op, anyNode.value);
    }
    if (type === 'cart_item_count') {
      if (!cart) return null;
      const count = Number.isFinite(Number(cart.item_count))
        ? Number(cart.item_count)
        : Array.isArray(cart.items)
          ? cart.items.reduce((/** @type {number} */ sum, /** @type {any} */ item) => sum + (Number(item?.quantity) || 0), 0)
          : 0;
      return compareNumber(count, anyNode.op, anyNode.value);
    }
    if (type === 'cart_has_variant') {
      if (!cart) return null;
      const variantId = Number(anyNode.value);
      if (!Number.isFinite(variantId)) return false;
      if (!Array.isArray(cart.items)) return false;
      return cart.items.some((/** @type {any} */ item) => Number(item?.variant_id) === variantId);
    }
    if (type === 'cart_has_product') {
      if (!cart) return null;
      const productId = Number(anyNode.value);
      if (!Number.isFinite(productId)) return false;
      if (!Array.isArray(cart.items)) return false;
      return cart.items.some((/** @type {any} */ item) => Number(item?.product_id) === productId);
    }

    return false;
  }

  /** @param {Element} element */
  #resumeDefaultAction(element) {
    if (element instanceof HTMLAnchorElement) {
      if (element.href) window.location.href = element.href;
      return;
    }

    const submitEl =
      element instanceof HTMLButtonElement || element instanceof HTMLInputElement ? element : null;
    if (!submitEl) {
      this.#resumeClickHandlers(element);
      return;
    }

    const type = submitEl instanceof HTMLButtonElement ? (submitEl.getAttribute('type') || 'submit') : submitEl.type;
    if (String(type).toLowerCase() !== 'submit') {
      this.#resumeClickHandlers(element);
      return;
    }

    const form =
      submitEl.form ||
      (submitEl.getAttribute('form') ? document.getElementById(submitEl.getAttribute('form') || '') : null);

    if (!(form instanceof HTMLFormElement)) {
      this.#resumeClickHandlers(element);
      return;
    }

    resumeSubmitGuards.add(form);
    setTimeout(() => resumeSubmitGuards.delete(form), 0);

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit(submitEl);
    } else {
      form.submit();
    }
    return;
  }

  /** @param {Element} element */
  #resumeClickHandlers(element) {
    if (!(element instanceof HTMLElement)) return;
    resumeClickGuards.add(element);
    setTimeout(() => resumeClickGuards.delete(element), 0);
    element.click();
  }

  /**
   * @param {Event} event
   * @param {any} evt
   * @param {Element} matchedElement
   */
  #handleMatchedBasicTriggerSync(event, evt, matchedElement) {
    if (this.refs.dialog.open) return;
    if (this.#shouldBlockClickOpenSync(false)) return;

    const result = this.#evaluateBasicClickConditionSync();
    if (result === false) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    evt.__tinkerMarketingPopupHandled = true;

    if (result === true) {
      this.#pendingAction = { element: matchedElement };
      this.showDialog({ isAuto: false });
      if (this.#clickOpenOncePerSessionEnabled()) this.#markOpenedOnClickThisSession();
      return;
    }

    setTimeout(async () => {
      if (!this.isConnected) return;
      if (this.refs.dialog.open) return;
      if (this.#shouldBlockClickOpenSync(false)) {
        this.#resumeDefaultAction(matchedElement);
        return;
      }

      const canOpen = await this.#evaluateBasicClickCondition();
      if (canOpen) {
        this.#pendingAction = { element: matchedElement };
        this.showDialog({ isAuto: false });
        if (this.#clickOpenOncePerSessionEnabled()) this.#markOpenedOnClickThisSession();
      } else {
        this.#resumeDefaultAction(matchedElement);
      }
    }, 0);
  }

  /**
   * @param {Event} event
   * @param {any} evt
   * @param {Element} matchedElement
   * @param {any} trigger
   */
  #handleMatchedTriggerSync(event, evt, matchedElement, trigger) {
    if (this.refs.dialog.open) return;

    const triggerOncePerSession = trigger?.oncePerSession === true;
    if (this.#shouldBlockClickOpenSync(triggerOncePerSession)) return;

    const result = this.#evaluateConditionNodeSync(trigger?.conditions);
    if (result === false) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    evt.__tinkerMarketingPopupHandled = true;

    if (result === true) {
      this.#pendingAction = { element: matchedElement };
      this.showDialog({ isAuto: false });
      if (this.#clickOpenOncePerSessionEnabled() || triggerOncePerSession) this.#markOpenedOnClickThisSession();
      return;
    }

    setTimeout(async () => {
      if (!this.isConnected) return;
      if (this.refs.dialog.open) return;
      if (this.#shouldBlockClickOpenSync(triggerOncePerSession)) {
        this.#resumeDefaultAction(matchedElement);
        return;
      }

      const canOpen = await this.#evaluateConditionNode(trigger?.conditions);
      if (canOpen) {
        this.#pendingAction = { element: matchedElement };
        this.showDialog({ isAuto: false });
        if (this.#clickOpenOncePerSessionEnabled() || triggerOncePerSession) this.#markOpenedOnClickThisSession();
      } else {
        this.#resumeDefaultAction(matchedElement);
      }
    }, 0);
  }

  /**
   * @param {MouseEvent} event
   * @param {any} evt
   */
  async #tryHandleBasicClickTrigger(event, evt) {
    if (this.refs.dialog.open) return false;
    if (await this.#shouldBlockClickOpen({ triggerOncePerSession: false })) return false;

    const canOpen = await this.#evaluateBasicClickCondition();
    if (!canOpen) return false;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    evt.__tinkerMarketingPopupHandled = true;
    this.showDialog({ isAuto: false });
    if (this.#clickOpenOncePerSessionEnabled()) this.#markOpenedOnClickThisSession();
    return true;
  }

  /**
   * @param {MouseEvent} event
   * @param {any} evt
   */
  async #tryHandleAdvancedClickTriggers(event, evt) {
    if (!this.#clickTriggers || !this.#clickTriggers.length) return false;
    if (!(event.target instanceof Element)) return false;

    for (const trigger of this.#clickTriggers) {
      const selector = typeof trigger?.selector === 'string' ? trigger.selector.trim() : '';
      if (!selector) continue;
      if (!event.target.closest(selector)) continue;

      const triggerOncePerSession = trigger?.oncePerSession === true;
      if (await this.#shouldBlockClickOpen({ triggerOncePerSession })) return false;

      const conditionsNode = trigger?.conditions;
      const canOpen = await this.#evaluateConditionNode(conditionsNode);
      if (!canOpen) return false;

      if (this.refs.dialog.open) return false;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      evt.__tinkerMarketingPopupHandled = true;
      this.showDialog({ isAuto: false });

      if (this.#clickOpenOncePerSessionEnabled() || triggerOncePerSession) this.#markOpenedOnClickThisSession();
      return true;
    }

    return false;
  }

  /** @param {{ triggerOncePerSession: boolean }} options */
  async #shouldBlockClickOpen({ triggerOncePerSession }) {
    if (!this.#clickOpenOncePerSessionEnabled() && !triggerOncePerSession) return false;
    return this.#hasOpenedOnClickThisSession();
  }

  async #evaluateBasicClickCondition() {
    const type = (this.dataset.clickConditionType || 'none').trim() || 'none';
    if (type === 'none') return true;

    if (type === 'customer_logged_in') return this.dataset.customerLoggedIn === 'true';
    if (type === 'customer_logged_out') return this.dataset.customerLoggedIn !== 'true';
    if (type === 'template_equals') {
      const expected = String(this.dataset.clickConditionValue || '').trim();
      if (!expected) return false;
      return getCurrentTemplate() === expected;
    }
    if (type === 'url_contains') {
      const expected = String(this.dataset.clickConditionValue || '').trim();
      if (!expected) return false;
      return window.location.href.includes(expected);
    }
    if (type === 'device_desktop') return isDesktopViewport();
    if (type === 'device_mobile') return isMobileViewport();

    if (
      type === 'cart_total_under' ||
      type === 'cart_total_over' ||
      type === 'cart_item_count_under' ||
      type === 'cart_item_count_over'
    ) {
      const cart = await getCartForConditions({ waitForUpdateMs: 1200 });
      if (!cart || typeof cart !== 'object') return false;

      if (type === 'cart_total_under' || type === 'cart_total_over') {
        const threshold = this.#parseMoneyToCents(this.dataset.clickConditionValue);
        if (threshold == null) return false;
        const total = Number(cart.total_price);
        if (!Number.isFinite(total)) return false;
        return type === 'cart_total_under' ? total < threshold : total > threshold;
      }

      const threshold = Number(String(this.dataset.clickConditionValue || '').trim());
      if (!Number.isFinite(threshold)) return false;
      const count = Number.isFinite(Number(cart.item_count))
        ? Number(cart.item_count)
        : Array.isArray(cart.items)
          ? cart.items.reduce((/** @type {number} */ sum, /** @type {any} */ item) => sum + (Number(item?.quantity) || 0), 0)
          : 0;
      return type === 'cart_item_count_under' ? count < threshold : count > threshold;
    }

    return false;
  }

  /**
   * @param {any} node
   * @returns {Promise<boolean>}
   */
  async #evaluateConditionNode(node) {
    if (node == null) return true;

    if (Array.isArray(node)) {
      for (const child of node) {
        const ok = await this.#evaluateConditionNode(child);
        if (!ok) return false;
      }
      return true;
    }

    if (typeof node !== 'object') return Boolean(node);

    const anyNode = /** @type {any} */ (node);
    if (Array.isArray(anyNode.all)) {
      for (const child of anyNode.all) {
        const ok = await this.#evaluateConditionNode(child);
        if (!ok) return false;
      }
      return true;
    }
    if (Array.isArray(anyNode.any)) {
      for (const child of anyNode.any) {
        const ok = await this.#evaluateConditionNode(child);
        if (ok) return true;
      }
      return false;
    }
    if (anyNode.not != null) {
      return !(await this.#evaluateConditionNode(anyNode.not));
    }

    const type = typeof anyNode.type === 'string' ? anyNode.type.trim() : '';
    if (!type) return false;

    if (type === 'customer_logged_in') {
      const expected = anyNode.value == null ? true : Boolean(anyNode.value);
      const actual = this.dataset.customerLoggedIn === 'true';
      return actual === expected;
    }

    if (type === 'page_template') {
      const expected = String(anyNode.value || '').trim();
      if (!expected) return false;
      return getCurrentTemplate() === expected;
    }

    if (type === 'url_contains') {
      const expected = String(anyNode.value || '').trim();
      if (!expected) return false;
      return window.location.href.includes(expected);
    }

    if (type === 'path_equals') {
      const expected = String(anyNode.value || '').trim();
      if (!expected) return false;
      return window.location.pathname === expected;
    }

    if (type === 'device_desktop') return isDesktopViewport();
    if (type === 'device_mobile') return isMobileViewport();

    if (type === 'cart_total_price') {
      const cart = await getCartForConditions({ waitForUpdateMs: 1200 });
      if (!cart || typeof cart !== 'object') return false;
      return compareNumber(cart.total_price, anyNode.op, anyNode.value);
    }

    if (type === 'cart_item_count') {
      const cart = await getCartForConditions({ waitForUpdateMs: 1200 });
      if (!cart || typeof cart !== 'object') return false;
      const count = Number.isFinite(Number(cart.item_count))
        ? Number(cart.item_count)
        : Array.isArray(cart.items)
          ? cart.items.reduce((/** @type {number} */ sum, /** @type {any} */ item) => sum + (Number(item?.quantity) || 0), 0)
          : 0;
      return compareNumber(count, anyNode.op, anyNode.value);
    }

    if (type === 'cart_has_variant') {
      const cart = await getCartForConditions({ waitForUpdateMs: 1200 });
      if (!cart || typeof cart !== 'object') return false;
      const variantId = Number(anyNode.value);
      if (!Number.isFinite(variantId)) return false;
      if (!Array.isArray(cart.items)) return false;
      return cart.items.some((/** @type {any} */ item) => Number(item?.variant_id) === variantId);
    }

    if (type === 'cart_has_product') {
      const cart = await getCartForConditions({ waitForUpdateMs: 1200 });
      if (!cart || typeof cart !== 'object') return false;
      const productId = Number(anyNode.value);
      if (!Number.isFinite(productId)) return false;
      if (!Array.isArray(cart.items)) return false;
      return cart.items.some((/** @type {any} */ item) => Number(item?.product_id) === productId);
    }

    return false;
  }

  #handleClick = (/** @type {MouseEvent} */ event) => {
    const allowOutsideClick = this.dataset.closeOnOutside !== 'false';
    if (!allowOutsideClick) return;
    const { dialog } = this.refs;
    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  #cookieName() {
    return `tinker_marketing_popup_${this.id || this.dataset.popupId || this.getAttribute('data-popup-id') || ''}`.replace(
      /[^a-zA-Z0-9_]/g,
      '_'
    );
  }

  #cookiePath() {
    const configured = this.dataset.cookiePath || '/';
    if (configured === 'page') return window.location.pathname || '/';
    return configured;
  }

  /** @param {string} name */
  #getCookie(name) {
    const prefix = `${encodeURIComponent(name)}=`;
    const cookies = document.cookie ? document.cookie.split('; ') : [];
    for (const cookie of cookies) {
      if (cookie.startsWith(prefix)) return decodeURIComponent(cookie.slice(prefix.length));
    }
    return null;
  }

  /**
   * @param {string} name
   * @param {string} value
   * @param {number} days
   * @param {string} path
   */
  #setCookie(name, value, days, path) {
    const encodedName = encodeURIComponent(name);
    const encodedValue = encodeURIComponent(value);

    let cookie = `${encodedName}=${encodedValue}; path=${path}; SameSite=Lax`;
    if (days > 0) {
      const expires = new Date(Date.now() + days * 864e5).toUTCString();
      cookie += `; expires=${expires}`;
    }

    document.cookie = cookie;
  }

  #shouldBlockAutoOpen() {
    const useCookieLock = this.dataset.useCookieLock !== 'false';
    if (!useCookieLock) return false;

    const cookieName = this.#cookieName();
    if (!cookieName) return false;

    return Boolean(this.#getCookie(cookieName));
  }

  #setCookieIfNeeded() {
    const useCookieLock = this.dataset.useCookieLock !== 'false';
    if (!useCookieLock) return;

    const cookieName = this.#cookieName();
    if (!cookieName) return;

    const days = Number(this.dataset.cookieDays || 0);
    const path = this.#cookiePath();
    this.#setCookie(cookieName, '1', days, path);
  }

  /**
   * @param {number} scrollY
   */
  #lockScroll(scrollY) {
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    this.#locked = true;
  }

  #unlockScroll() {
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    this.#locked = false;
  }

  #focusForOpen() {
    const { dialog } = this.refs;
    const root = dialog.querySelector('.marketing-popup__body') || dialog;
    const focusables = root.querySelectorAll(
      'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
    );

    const first = Array.from(focusables).find((el) => el instanceof HTMLElement && el.getClientRects().length > 0);
    if (first instanceof HTMLElement) {
      first.focus({ preventScroll: true });
      return;
    }

    const closeButton = dialog.querySelector('[ref="closeButton"]');
    if (closeButton instanceof HTMLElement) {
      closeButton.focus({ preventScroll: true });
      return;
    }

    dialog.focus({ preventScroll: true });
  }

  #restoreFocus() {
    const el = this.#lastFocusedElement;
    if (!(el instanceof HTMLElement)) return;
    if (!document.contains(el)) return;
    el.focus({ preventScroll: true });
    this.#lastFocusedElement = null;
  }

  #editorStateKey() {
    const id = (this.dataset.popupId || this.getAttribute('data-popup-id') || this.id || '').replace(/[^a-zA-Z0-9_]/g, '_');
    return `editor-save-state-marketing-popup-${id}`;
  }

  #getEditorOpenState() {
    try {
      return sessionStorage.getItem(this.#editorStateKey()) === 'true';
    } catch {
      return false;
    }
  }

  /** @param {boolean} isOpen */
  #setEditorOpenState(isOpen) {
    try {
      if (isOpen) {
        sessionStorage.setItem(this.#editorStateKey(), 'true');
      } else {
        sessionStorage.removeItem(this.#editorStateKey());
      }
    } catch {
      return;
    }
  }
}

if (!customElements.get('marketing-popup')) customElements.define('marketing-popup', MarketingPopup);
